import { useEffect, useRef } from "react";
import SockJS from "sockjs-client";
import { Client } from "@stomp/stompjs";
import { guardURL } from "../../../../Apis/Constants";
import { MODELS_NAME } from "../../../../Apis/Models";
import { EVENT_MONITORING_SERVICE } from "../../../../reduxToolkit/EndPoints";
import { EVENT_MODEL } from "../Model/Model";

/**
 * @Author      Karen Yunuen Pozos Arias
 * @JobTitle    Full Stack
 * @Email       kpozos@ibl.mx
 *
 * Connection of the Event Monitoring topic, with the same lifecycle as DailyServicesWebSocket.
 *
 * Every message of the topic hands its new events to onEvents, which places them in the panels
 * they belong to; the reports of every user come with their own model and go to onReports. Once
 * connected it calls onConnected, so the panels read again their first page and no event published
 * while the connection was lost is missed.
 *
 * @param {boolean} isOnline - Connection flag.
 * @param {Function} setIsOnline - Setter of the connection flag.
 * @param {Function} onEvents - Receives the new events of every message.
 * @param {Function} onReports - Receives the reports created or updated by any user.
 * @param {Function} onConnected - Called every time the connection is established.
 * @returns {{reconnectWebSocket: Function}}
 */
export const EventMonitoringWebSocket = ({ isOnline, setIsOnline, onEvents, onReports, onConnected }) => {
    const subscriptionsRef = useRef([]);
    const clientRef = useRef(null);
    const reconnectTimeoutRef = useRef(null);
    const connectionTimeoutRef = useRef(null);
    const isReconnectingRef = useRef(false);
    const connectionSuccessRef = useRef(false);
    const isMountedRef = useRef(false);

    /**
     * Fully closes client + underlying socket + timeouts
     */
    const destroyWebSocket = async () => {
        if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
            reconnectTimeoutRef.current = null;
        }

        if (connectionTimeoutRef.current) {
            clearTimeout(connectionTimeoutRef.current);
            connectionTimeoutRef.current = null;
        }

        subscriptionsRef.current.forEach((sub) => {
            try {
                sub.unsubscribe();
            } catch (e) {}
        });
        subscriptionsRef.current = [];

        const client = clientRef.current;
        if (!client) {
            isReconnectingRef.current = false;
            connectionSuccessRef.current = false;
            return;
        }

        try {
            await client.deactivate();
        } catch (e) {}

        try {
            if (client.webSocket) client.webSocket.close();
            else if (client._webSocket) client._webSocket.close();
        } catch (e) {}

        clientRef.current = null;
        isReconnectingRef.current = false;
        connectionSuccessRef.current = false;
    };

    /**
     * Schedule a reconnect if not already pending
     */
    const scheduleReconnect = (delay = 5000) => {
        if (!isMountedRef.current) return;
        if (reconnectTimeoutRef.current || isReconnectingRef.current) return;

        isReconnectingRef.current = true;

        reconnectTimeoutRef.current = setTimeout(async () => {
            reconnectTimeoutRef.current = null;

            if (!isMountedRef.current) {
                isReconnectingRef.current = false;
                return;
            }

            await destroyWebSocket();
            connectWebSocket();
        }, delay);
    };

    /**
     * Marks the connection as lost and schedules a reconnection.
     */
    const handleConnectionLost = () => {
        if (!isMountedRef.current) return;

        setIsOnline(false);
        connectionSuccessRef.current = false;
        scheduleReconnect();
    };

    /**
     * Initialize socket connection
     */
    const connectWebSocket = () => {
        if (!isMountedRef.current) return;
        if (clientRef.current?.active || clientRef.current?.connected) return;

        const client = new Client({
            webSocketFactory: () => new SockJS(guardURL),
            debug: () => {},
            reconnectDelay: 0,
            heartbeatIncoming: 10000,
            heartbeatOutgoing: 10000,

            onConnect: () => {
                if (!isMountedRef.current) return;

                setIsOnline(true);
                connectionSuccessRef.current = true;
                isReconnectingRef.current = false;

                if (reconnectTimeoutRef.current) {
                    clearTimeout(reconnectTimeoutRef.current);
                    reconnectTimeoutRef.current = null;
                }

                if (connectionTimeoutRef.current) {
                    clearTimeout(connectionTimeoutRef.current);
                    connectionTimeoutRef.current = null;
                }

                subscriptionsRef.current.forEach((sub) => {
                    try {
                        sub.unsubscribe();
                    } catch (e) {}
                });
                subscriptionsRef.current = [];

                const subscription = client.subscribe(
                    EVENT_MONITORING_SERVICE.topicEvents,
                    (message) => {
                        let parsed;

                        try {
                            parsed = JSON.parse(message.body);
                        } catch (e) {
                            return;
                        }

                        if (!Array.isArray(parsed?.data)) return;

                        // The reports come with their own model instead of events //
                        if (parsed.model === MODELS_NAME.reportLog) {
                            onReports(parsed.data);
                            return;
                        }
                        onEvents(parsed.data.map(EVENT_MODEL.fromApi));
                    }
                );

                subscriptionsRef.current = [subscription];
                // The panels are read again after subscribing, so no event is lost in between //
                onConnected();
            },

            onStompError: handleConnectionLost,
            onWebSocketClose: handleConnectionLost,
            onWebSocketError: handleConnectionLost
        });

        clientRef.current = client;
        client.activate();

        connectionTimeoutRef.current = setTimeout(async () => {
            if (!isMountedRef.current) return;

            const currentClient = clientRef.current;
            if (currentClient === client && !client.connected && !connectionSuccessRef.current) {
                await destroyWebSocket();
                scheduleReconnect(4000);
            }
        }, 10000);
    };

    /**
     * Mount / unmount lifecycle
     */
    useEffect(() => {
        isMountedRef.current = true;
        connectWebSocket();

        return () => {
            isMountedRef.current = false;
            destroyWebSocket();
        };
    }, []);

    /**
     * Manual reconnect
     */
    const reconnectWebSocket = async () => {
        if (isOnline || clientRef.current?.connected) return;

        await destroyWebSocket();
        connectWebSocket();
    };

    return { reconnectWebSocket };
};
